import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Observable, forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';
import { WorkflowDesignerState, NodePosition, WorkflowDraft, initialState } from './workflow.state';
import {
  CanvasViewport,
  NodeSize,
  WorkflowOrientation,
  arrangeLaidOutNodes,
  estimateCardSize,
  layoutWorkflow,
  WORKFLOW_LAYOUT_VERSION
} from './workflow-layout';
import {
  Workflow,
  WorkflowNode,
  WorkflowNodeType,
  WorkflowNodeRuleType,
  WorkflowNodeActionType,
  UpsertWorkflowDto,
  UpsertWorkflowNodeDto,
  WorkflowResponseDto,
  generateGuid,
  validateWorkflow
} from '../models';
import { WorkflowApiService } from '../services/workflow-api.service';
import { WorkflowOrientationService } from '../services/workflow-orientation.service';

/** Arguments for creating a node from the palette. */
export interface CreateNodeArgs {
  type: WorkflowNodeType;
  ruleType?: WorkflowNodeRuleType;
  actionType?: WorkflowNodeActionType;
  position: NodePosition;
}

/**
 * Build a fresh node. A Terminator points its successor at itself (per the
 * domain contract); every other new node starts unconnected (empty successor).
 */
function createNode(workflowId: string, args: CreateNodeArgs): WorkflowNode {
  const id = generateGuid();
  return {
    id,
    workflowId,
    name: defaultNodeName(args),
    description: '',
    type: args.type,
    ruleType: args.type === WorkflowNodeType.Rule ? args.ruleType : undefined,
    actionType: args.type === WorkflowNodeType.Action ? args.actionType : undefined,
    requiredMetadata: [],
    definition: {},
    successorTrue: args.type === WorkflowNodeType.Terminator ? id : '',
    successorFalse: undefined
  };
}

function defaultNodeName(args: CreateNodeArgs): string {
  if (args.type === WorkflowNodeType.Rule && args.ruleType) return args.ruleType;
  if (args.type === WorkflowNodeType.Action && args.actionType) return args.actionType;
  return args.type;
}

/**
 * Feature-level signal store for the workflow designer. Follows the same
 * pattern as FormStore: `withState` holds the slice, `withComputed` exposes
 * derived selectors, and `withMethods` carries synchronous mutations
 * (`patchState`) plus async loaders (`rxMethod`).
 */
export const WorkflowStore = signalStore(
  { providedIn: 'root' },
  withState<WorkflowDesignerState>(initialState),

  withComputed(state => ({
    workflowName: computed(() => state.currentWorkflow()?.name || ''),
    workflowDescription: computed(() => state.currentWorkflow()?.description || ''),
    nodeCount: computed(() => state.nodes().length),
    selectedNode: computed(() => {
      const id = state.selectedNodeId();
      return id ? state.nodes().find(n => n.id === id) ?? null : null;
    }),
    validation: computed(() => validateWorkflow(state.nodes())),
    hasValidationErrors: computed(() => validateWorkflow(state.nodes()).errors.length > 0)
  })),

  withMethods((
    store,
    api = inject(WorkflowApiService),
    orientationPrefs = inject(WorkflowOrientationService)
  ) => ({
    // --- Synchronous mutations ---

    initializeWorkflow(name = ''): void {
      const workflow: Workflow = { id: generateGuid(), name, description: '' };
      patchState(store, {
        currentWorkflow: workflow,
        nodes: [],
        positions: {},
        layoutSlots: {},
        selectedNodeId: null,
        isPersisted: false,
        persistedNodeIds: [],
        error: null
      });
    },

    updateWorkflowName(name: string): void {
      const current = store.currentWorkflow();
      if (!current) return;
      patchState(store, { currentWorkflow: { ...current, name } });
    },

    updateWorkflowDescription(description: string): void {
      const current = store.currentWorkflow();
      if (!current) return;
      patchState(store, { currentWorkflow: { ...current, description } });
    },

    addNode(args: CreateNodeArgs): WorkflowNode | null {
      const current = store.currentWorkflow();
      if (!current) return null;
      const node = createNode(current.id, args);
      patchState(store, {
        nodes: [...store.nodes(), node],
        positions: { ...store.positions(), [node.id]: args.position },
        selectedNodeId: node.id
      });
      return node;
    },

    updateNode(node: WorkflowNode): void {
      const isCondition =
        node.type === WorkflowNodeType.Rule &&
        node.ruleType === WorkflowNodeRuleType.Condition;
      // Strip the false branch on anything that's not a Condition — the
      // executor only reads it there and leaving it set produces ghost edges.
      const sanitized: WorkflowNode = isCondition
        ? node
        : { ...node, successorFalse: undefined };
      patchState(store, {
        nodes: store.nodes().map(n => (n.id === node.id ? sanitized : n))
      });
    },

    deleteNode(nodeId: string): void {
      // Drop the node, its position, and any successor edges pointing at it.
      const nodes = store.nodes()
        .filter(n => n.id !== nodeId)
        .map(n => ({
          ...n,
          successorTrue: n.successorTrue === nodeId ? '' : n.successorTrue,
          successorFalse: n.successorFalse === nodeId ? undefined : n.successorFalse
        }));
      const positions = { ...store.positions() };
      delete positions[nodeId];
      const layoutSlots = { ...store.layoutSlots() };
      delete layoutSlots[nodeId];
      patchState(store, {
        nodes,
        positions,
        layoutSlots,
        selectedNodeId: store.selectedNodeId() === nodeId ? null : store.selectedNodeId()
      });
    },

    selectNode(nodeId: string | null): void {
      patchState(store, { selectedNodeId: nodeId });
    },

    /** Connect one node to another along its true or false branch. */
    connectNodes(fromId: string, toId: string, branch: 'true' | 'false' = 'true'): void {
      patchState(store, {
        nodes: store.nodes().map(n =>
          n.id === fromId
            ? branch === 'false'
              ? { ...n, successorFalse: toId }
              : { ...n, successorTrue: toId }
            : n
        )
      });
    },

    /** Clear an outgoing edge on the given branch of a node. */
    disconnectNodes(fromId: string, branch: 'true' | 'false' = 'true'): void {
      patchState(store, {
        nodes: store.nodes().map(n => {
          if (n.id !== fromId) return n;
          if (branch === 'false') return { ...n, successorFalse: undefined };
          // Terminator self-loop is part of the domain contract — don't clear it.
          if (n.type === WorkflowNodeType.Terminator) return n;
          return { ...n, successorTrue: '' };
        })
      });
    },

    setNodePosition(nodeId: string, position: NodePosition): void {
      // A dragged node is placed by hand from now on; pinning it (rather than
      // dropping its slot) keeps its column width so other cards stay put.
      const slots = store.layoutSlots();
      const slot = slots[nodeId];
      patchState(store, {
        positions: { ...store.positions(), [nodeId]: position },
        ...(slot && !slot.pinned
          ? { layoutSlots: { ...slots, [nodeId]: { ...slot, pinned: true } } }
          : {})
      });
    },

    /**
     * Turn the graph to flow across the screen or down it, and remember the
     * choice for next time. The whole graph is laid out afresh: a hand-placed
     * card has no meaningful position in the other orientation, so pins are
     * dropped rather than left stranded among the re-arranged cards.
     */
    setOrientation(orientation: WorkflowOrientation): void {
      if (store.orientation() === orientation) return;
      orientationPrefs.write(orientation);
      // Slots don't depend on orientation, so only the arrangement is redone —
      // with the sizes the canvas last reported, since turning it doesn't
      // resize a single card and so won't trigger a measuring pass of its own.
      const { slots } = layoutWorkflow(store.nodes(), orientation);
      const nodesById = new Map(store.nodes().map(node => [node.id, node]));
      const measured = store.measuredSizes();
      patchState(store, {
        orientation,
        layoutSlots: slots,
        positions: arrangeLaidOutNodes(
          slots,
          id => measured[id] ?? estimateCardSize(nodesById.get(id)!),
          orientation,
          store.canvasViewport()
        )
      });
    },

    /**
     * Record how big the canvas is, so the next layout can centre the graph in
     * it. Deliberately doesn't re-arrange on its own: cards sliding about while
     * the window is being dragged would be worse than a graph slightly off
     * centre until something else moves it.
     */
    setCanvasViewport(viewport: CanvasViewport): void {
      const current = store.canvasViewport();
      if (current?.width === viewport.width && current?.height === viewport.height) return;
      patchState(store, { canvasViewport: viewport });
    },

    /**
     * Re-space auto-laid-out cards using the sizes the canvas actually
     * rendered, so gaps are even and arrows level. Runs again whenever cards
     * resize (web fonts loading, a rename); dragged cards are left alone.
     */
    applyMeasuredSizes(sizes: ({ id: string } & NodeSize)[]): void {
      const slots = store.layoutSlots();
      if (Object.keys(slots).length === 0) return;

      const measured = Object.fromEntries(sizes.map(({ id, ...size }) => [id, size]));
      const nodesById = new Map(store.nodes().map(node => [node.id, node]));
      const arranged = arrangeLaidOutNodes(
        slots,
        id => measured[id] ?? estimateCardSize(nodesById.get(id)!),
        store.orientation(),
        store.canvasViewport()
      );

      const positions = store.positions();
      const moved = Object.entries(arranged).filter(
        ([id, pos]) => positions[id]?.x !== pos.x || positions[id]?.y !== pos.y
      );
      patchState(store, {
        measuredSizes: measured,
        ...(moved.length ? { positions: { ...positions, ...Object.fromEntries(moved) } } : {})
      });
    },

    /**
     * Hydrate the designer from a locally persisted draft (session state)
     * without touching the API. Drafts saved under an older layout scheme
     * (e.g. the original fixed grid), or turned the other way since they were
     * saved, are laid out afresh; the rest keep their positions, including
     * hand-placed ones.
     */
    restoreDraft(draft: WorkflowDraft): void {
      const orientation = store.orientation();
      const stale =
        draft.layoutVersion !== WORKFLOW_LAYOUT_VERSION || draft.layoutOrientation !== orientation;
      const layout = stale
        ? layoutWorkflow(draft.nodes, orientation, store.canvasViewport())
        : null;
      patchState(store, {
        currentWorkflow: draft.workflow,
        nodes: draft.nodes,
        positions: layout ? layout.positions : draft.positions,
        layoutSlots: layout ? layout.slots : (draft.layoutSlots ?? {}),
        isPersisted: draft.isPersisted,
        // Older drafts predate persistedNodeIds; assume everything present at
        // snapshot time was persisted when the workflow itself was.
        persistedNodeIds:
          draft.persistedNodeIds ??
          (draft.isPersisted ? draft.nodes.map(n => n.id) : []),
        selectedNodeId: null,
        loading: false,
        error: null
      });
    },

    // --- Async loaders ---

    loadWorkflows: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { loadingWorkflows: true, workflowsError: null })),
        switchMap(() =>
          api.listWorkflows().pipe(
            tapResponse({
              next: workflows =>
                patchState(store, {
                  workflows: workflows as Workflow[],
                  loadingWorkflows: false,
                  workflowsError: null
                }),
              error: (error: { message?: string }) =>
                patchState(store, {
                  loadingWorkflows: false,
                  workflowsError: error?.message || 'Failed to load workflows'
                })
            })
          )
        )
      )
    ),

    /**
     * Load another workflow's nodes to show beside the list, without touching
     * the workflow being edited. switchMap drops a superseded selection.
     */
    loadWorkflowPreview: rxMethod<string>(
      pipe(
        tap(workflowId =>
          patchState(store, {
            previewWorkflowId: workflowId,
            previewNodes: [],
            loadingPreview: true,
            previewError: null
          })
        ),
        switchMap(workflowId =>
          api.getWorkflowNodes(workflowId).pipe(
            tapResponse({
              next: nodes =>
                patchState(store, {
                  previewNodes: nodes as unknown as WorkflowNode[],
                  loadingPreview: false,
                  previewError: null
                }),
              error: (error: { message?: string }) =>
                patchState(store, {
                  loadingPreview: false,
                  previewError: error?.message || 'This workflow could not be loaded.'
                })
            })
          )
        )
      )
    ),

    clearWorkflowPreview(): void {
      patchState(store, {
        previewWorkflowId: null,
        previewNodes: [],
        loadingPreview: false,
        previewError: null
      });
    },

    loadWorkflow: rxMethod<string>(
      pipe(
        tap(() => patchState(store, { loading: true, error: null })),
        switchMap(workflowId =>
          forkJoin({
            workflow: api.getWorkflow(workflowId),
            nodes: api.getWorkflowNodes(workflowId)
          }).pipe(
            tapResponse({
              next: ({ workflow, nodes }) => {
                const wfNodes = nodes as unknown as WorkflowNode[];
                const layout = layoutWorkflow(wfNodes, store.orientation(), store.canvasViewport());
                patchState(store, {
                  currentWorkflow: workflow as Workflow,
                  nodes: wfNodes,
                  positions: layout.positions,
                  layoutSlots: layout.slots,
                  isPersisted: true,
                  persistedNodeIds: wfNodes.map(n => n.id),
                  loading: false,
                  error: null,
                  selectedNodeId: null
                });
              },
              error: (error: { message?: string }) =>
                patchState(store, {
                  loading: false,
                  error: error?.message || 'Failed to load workflow'
                })
            })
          )
        )
      )
    ),

    /**
     * Save the current workflow + its nodes to the API.
     *
     * The node controller is per-entity CRUD with server-assigned ids, so
     * the graph is saved in phases:
     *  1. Upsert the workflow metadata (POST first time, PUT after).
     *  2. POST each new node with placeholder successors, building a
     *     client-id → server-id map from the responses.
     *  3. PUT every node with successors remapped through that map.
     *  4. DELETE server nodes that no longer exist locally.
     * Finally local state (ids, positions, selection) is remapped to the
     * server ids so subsequent saves diff correctly.
     */
    saveWorkflow: rxMethod<void>(
      pipe(
        tap(() => patchState(store, { saving: true, error: null })),
        switchMap(() => {
          const workflow = store.currentWorkflow();
          if (!workflow) {
            patchState(store, { saving: false });
            return of(null);
          }
          if (store.hasValidationErrors()) {
            patchState(store, {
              saving: false,
              error: 'Fix validation errors before saving.'
            });
            return of(null);
          }

          const upsert: UpsertWorkflowDto = {
            name: workflow.name,
            description: workflow.description
          };
          const writeMetadata$ = store.isPersisted()
            ? api.updateWorkflow(workflow.id, upsert)
            : api.createWorkflow(upsert);

          return writeMetadata$.pipe(
            switchMap((saved: WorkflowResponseDto) =>
              persistNodeGraph(api, saved.id, store.nodes(), store.persistedNodeIds()).pipe(
                map(result => ({ workflowId: saved.id, ...result }))
              )
            ),
            tapResponse({
              next: ({ workflowId, nodes, idMap }) => {
                const positions: Record<string, NodePosition> = {};
                for (const [oldId, pos] of Object.entries(store.positions())) {
                  positions[idMap.get(oldId) ?? oldId] = pos;
                }
                const selected = store.selectedNodeId();
                patchState(store, {
                  currentWorkflow: { ...store.currentWorkflow()!, id: workflowId },
                  nodes,
                  positions,
                  selectedNodeId: selected ? idMap.get(selected) ?? selected : null,
                  isPersisted: true,
                  persistedNodeIds: nodes.map(n => n.id),
                  saving: false,
                  error: null
                });
              },
              error: (error: { message?: string }) =>
                patchState(store, {
                  saving: false,
                  error: error?.message || 'Failed to save workflow'
                })
            })
          );
        })
      )
    )
  })),

  withHooks({
    /** The orientation preference outlives the session, so pick it up on start. */
    onInit(store, orientationPrefs = inject(WorkflowOrientationService)) {
      patchState(store, { orientation: orientationPrefs.read() });
    }
  })
);

function toUpsertNode(n: WorkflowNode, workflowId: string): UpsertWorkflowNodeDto {
  return {
    workflowId,
    name: n.name,
    description: n.description,
    type: n.type,
    ruleType: n.ruleType,
    actionType: n.actionType,
    requiredMetadata: n.requiredMetadata,
    definition: n.definition,
    successorTrue: n.successorTrue,
    successorFalse: n.successorFalse
  };
}

/**
 * Persist the node graph against the per-entity CRUD endpoints.
 *
 * Phase 1 creates new nodes with successor references blanked when they
 * point at other not-yet-created nodes (the server assigns ids, so those
 * references can't be expressed yet). Phase 2 PUTs every node with the
 * final successor wiring remapped through the returned id map. Phase 3
 * deletes server nodes removed locally.
 */
function persistNodeGraph(
  api: WorkflowApiService,
  workflowId: string,
  localNodes: WorkflowNode[],
  persistedNodeIds: string[]
): Observable<{ nodes: WorkflowNode[]; idMap: Map<string, string> }> {
  const persisted = new Set(persistedNodeIds);
  const newNodes = localNodes.filter(n => !persisted.has(n.id));
  const localIds = new Set(localNodes.map(n => n.id));
  const removedIds = persistedNodeIds.filter(id => !localIds.has(id));

  // Phase 1: create new nodes; blank successor refs to other new nodes
  // (including terminator self-loops) until server ids are known.
  const create$: Observable<Map<string, string>> =
    newNodes.length === 0
      ? of(new Map<string, string>())
      : forkJoin(
          newNodes.map(n => {
            const dto = toUpsertNode(n, workflowId);
            const blankIfNew = (ref: string | null | undefined) =>
              ref && (persisted.has(ref) ? ref : '');
            return api
              .createWorkflowNode({
                ...dto,
                successorTrue: blankIfNew(n.successorTrue) || '',
                successorFalse: blankIfNew(n.successorFalse) || null
              })
              .pipe(map(created => [n.id, created.id] as const));
          })
        ).pipe(map(pairs => new Map(pairs)));

  return create$.pipe(
    switchMap(idMap => {
      const remap = (ref: string | null | undefined): string =>
        ref ? idMap.get(ref) ?? ref : '';

      const finalNodes: WorkflowNode[] = localNodes.map(n => ({
        ...n,
        id: remap(n.id) || n.id,
        workflowId,
        successorTrue: remap(n.successorTrue),
        successorFalse: n.successorFalse ? remap(n.successorFalse) : undefined
      }));

      // Phase 2: PUT everything with final wiring.
      const update$ =
        finalNodes.length === 0
          ? of([] as unknown[])
          : forkJoin(
              finalNodes.map(n => api.updateWorkflowNode(n.id, toUpsertNode(n, workflowId)))
            );

      // Phase 3: delete removed nodes.
      const delete$ =
        removedIds.length === 0
          ? of([] as unknown[])
          : forkJoin(removedIds.map(id => api.deleteWorkflowNode(id)));

      return forkJoin([update$, delete$]).pipe(
        map(() => ({ nodes: finalNodes, idMap }))
      );
    })
  );
}
